<template>
  <div>
    <mf-card-container class="header-card-container">
      <div class="header-form-row">
        <v-text-field
          v-model="campaign.title"
          label="Título da campanha"
          outlined
          :maxlength="70"
          counter
          :disabled="disableTitle"
          :error="titleErrors.length > 0"
          :error-messages="titleErrors"
          class="title-field"
          @blur="validations.voucher.title.$touch()"
        />
        <v-text-field
          v-model="campaign.description"
          label="Descrição"
          outlined
          :maxlength="150"
          counter
          :disabled="disableDescription"
          :error="descriptionErrors.length > 0"
          :error-messages="descriptionErrors"
          class="description-field"
          @blur="validations.voucher.description.$touch()"
        />
      </div>
    </mf-card-container>

    <mf-card-container>
      <div class="main-content-row">
        <div class="stores-section">
          <div class="section-header">
            <span class="section-title">Lojas</span>
            <div class="section-description">Lojas participantes deste vale-troca:</div>
          </div>
          <div class="stores-selector">
            <mf-select-units-dialog
              id="campaign-units"
              outlined
              :selected-units.sync="campaign.units"
              :unit-error="unitsErrors.length > 0"
              required
              :units="units"
              :tag-list="tagList"
              :group-list="unitsGroups"
              :disabled="disableUnits"
              hide-ecommerce-configuration
              class="units-selector"
            />
          </div>
        </div>

        <div class="dates-section">
          <div class="section-header">
            <span class="section-title">Data de início e fim</span>
            <div class="section-description">Defina o período em que este vale-troca poderá ser ativado. Vales-troca ativados têm validade de 30 dias</div>
          </div>
          <div class="dates-row">
            <date-picker
              label="Data de início"
              :date.sync="initialDate"
              outlined
              prepend-icon="calendar_today"
              :disabled="disableInitialDate"
              :min="todayDate"
              class="date-picker-start"
              :validations="validations"
              validation-path="voucher.initial_date"
              :error-messages="initialDateErrors"
            />

            <date-picker
              label="Data de fim"
              :date.sync="finalDate"
              outlined
              prepend-icon="calendar_today"
              :disabled="disableFinalDate"
              :min="todayDate"
              class="date-picker-end"
              :validations="validations"
              validation-path="voucher.final_date"
              :error-messages="finalDateErrors"
            />
          </div>
        </div>
      </div>
      <mf-info-card
        v-if="isActiveCampaign"
        variant="error"
        title="Vales já ativados em lojas removidas continuarão válidos e disponíveis para uso pelos clientes."
        icon="warning"
        class="info-card"
        max-width="750px"
      />
    </mf-card-container>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import DatePicker from '@/components/DatePicker.vue'

export default {
  name: 'Header',
  components: {
    DatePicker,
  },
  props: {
    campaign: { type: Object, required: true },
    validations: { type: Object, required: true },
  },
  computed: {
    ...mapGetters(['units', 'tagList', 'unitsGroups']),
    disableTitle() {
      return ['FINISHED'].includes(this.campaign.status)
    },
    disableDescription() {
      return ['FINISHED'].includes(this.campaign.status)
    },
    disableInitialDate() {
      return ['FINISHED', 'ACTIVE'].includes(this.campaign.status)
    },
    disableFinalDate() {
      return ['FINISHED'].includes(this.campaign.status)
    },
    disableUnits() {
      return ['FINISHED'].includes(this.campaign.status)
    },
    isActiveCampaign() {
      return this.campaign.status === 'ACTIVE'
    },
    todayDate() {
      return new Date().toISOString().split('T')[0]
    },
    titleErrors() {
      const val = this.validations?.voucher?.title
      if (!val || !val.$dirty || !val.$error) return []
      const errors = []
      if (!val.required) errors.push('Dê um nome para este vale-troca.')
      if (!val.minLength) errors.push('O título possui menos de 2 caracteres.')
      if (!val.maxLength) errors.push('O título excedeu 70 caracteres.')
      if (!val.notOnlySpace) errors.push('Não é permitido salvar apenas espaços.')
      return errors
    },
    descriptionErrors() {
      const val = this.validations?.voucher?.description
      if (!val || !val.$dirty || !val.$error) return []
      const errors = []
      if (!val.required) errors.push('Informe uma descrição.')
      if (!val.minLength) errors.push('A descrição possui menos de 2 caracteres.')
      if (!val.maxLength) errors.push('A descrição excedeu 150 caracteres.')
      if (!val.notOnlySpace) errors.push('Não é permitido salvar apenas espaços.')
      return errors
    },
    initialDateErrors() {
      const errors = []
      const { initial_date, final_date } = this.campaign

      if (initial_date && final_date && new Date(initial_date) > new Date(final_date)) {
        errors.push('A data de início não pode ser maior que a data de fim.')
      }

      const val = this.validations?.voucher?.initial_date
      if (val?.$dirty && val?.$error) {
        if (!val.required) errors.push('A data de início é obrigatória.')
        if (!val.initialDateNotPast) errors.push('A data de início não pode ser menor que o dia de hoje.')
      }

      return errors
    },
    finalDateErrors() {
      const errors = []
      const { initial_date, final_date } = this.campaign

      if (initial_date && final_date && new Date(initial_date) > new Date(final_date)) {
        errors.push('A data de fim não pode ser menor que a data de início.')
      }

      const val = this.validations?.voucher?.final_date
      if (val?.$dirty && val?.$error) {
        if (!val.required) errors.push('A data de fim é obrigatória.')
      }

      return errors
    },
    initialDate: {
      get() {
        return this.campaign.initial_date?.split('T')[0] || null
      },
      set(val) {
        if (val) {
          this.campaign.initial_date = `${val}T00:00:00.000Z`
        }
      },
    },
    finalDate: {
      get() {
        return this.campaign.final_date?.split('T')[0] || null
      },
      set(val) {
        if (val) {
          this.campaign.final_date = `${val}T23:59:59.999Z`
        }
      },
    },
    unitsErrors() {
      const val = this.validations?.voucher?.units
      if (!val || !val.$dirty || !val.$error) return []
      const errors = []
      if (!val.required) errors.push('Selecione pelo menos uma loja participante.')
      return errors
    },
  },
}
</script>

<style scoped>
.header-card-container {
  margin-bottom: 24px;
}

.header-form-row {
  display: flex;
  gap: 24px;
}

.title-field {
  width: 30%;
}

.description-field {
  width: 70%;
}

.main-content-row {
  display: flex;
  gap: 48px;
  align-items: flex-start;
}

.stores-section {
  flex: 1;
}

.section-header {
  margin-bottom: 4px;
}

.section-title {
  font-family: 'Lato';
  font-weight: 700;
  font-size: 20px;
  color: #4b525a;
}

.section-description {
  font-family: 'Lato';
  font-weight: 400;
  font-size: 14px;
  color: #656565;
  margin-top: 26px;
}

.stores-selector {
  margin-top: 16px;
}

.units-selector {
  width: 100%;
}

.dates-section {
  flex: 2;
}

.dates-row {
  display: flex;
  gap: 24px;
  margin-top: 16px;
}

.date-picker-start {
  width: 50%;
}

.date-picker-end {
  width: 50%;
}

.info-card {
  margin-top: 24px;

  .text-container {
    display: none !important;
  }
}
</style>
